/**
 * Public print-preview page for the NGO/partner Handout Card.
 * Route: /preview/handout/:slug
 *
 * Renders both card sides at native print size with no scale transform so it
 * can be captured to PDF via headless browser. Uses sensible defaults derived
 * from the partner record. Public (no auth) so an outreach email can link to
 * a "here's what your card would look like" preview.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { HandoutCardFront, HandoutCardBack } from '../components/business-card/HandoutCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HandoutCardPreview() {
  const { slug } = useParams();
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [partner, setPartner] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/ngos/${slug}`);
        setPartner(res.data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  // Cancel the default 0.3 scale-down of the card components so the PDF renders at native print size.
  useEffect(() => {
    [frontRef, backRef].forEach((r) => {
      if (r.current) {
        r.current.style.transform = 'none';
        r.current.style.transformOrigin = 'top left';
      }
    });
  }, [partner]);

  if (notFound) {
    return <div style={{ padding: 40, fontFamily: 'system-ui' }}>Partner not found.</div>;
  }
  if (!partner) {
    return <div style={{ padding: 40, fontFamily: 'system-ui' }} data-testid="handout-preview-loading">Loading…</div>;
  }

  const qrTarget = `https://americandogclub.com/join/${partner.slug}`;

  return (
    <div
      style={{
        background: '#f8fafc',
        padding: 20,
        minHeight: '100vh',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
      data-testid="handout-preview-page"
    >
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
            Handout Card preview — {partner.name}
          </h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>
            This is what your co-branded handout card would look like. Front-side QR sends new families to
            &nbsp;<code>/join/{partner.slug}</code>&nbsp; where they claim a full-year membership on us.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
          <div>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginBottom: 8 }}>
              Front
            </p>
            <HandoutCardFront
              ref={frontRef}
              ngoName={partner.name}
              ngoLogoSrc={partner.logo_url || undefined}
              qrTarget={qrTarget}
              ctaLine="Scan for your free year"
              headline="Welcome home."
              subhead={partner.tagline || 'Every dog deserves a community.'}
            />
          </div>

          <div>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginBottom: 8 }}>
              Back
            </p>
            <HandoutCardBack
              ref={backRef}
              ngoName={partner.name}
              ngoAddress={partner.address || ''}
              ngoPhone={partner.phone || ''}
              ngoEmail={partner.email || ''}
              ngoWebsite={partner.website || ''}
            />
          </div>
        </div>

        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 20 }}>
          1/4-page cards · 4.25"×5.5" · 1275×1650px @ 300 DPI · US Letter fits 4 up
        </p>
      </div>
    </div>
  );
}
